import { subscriptionDcaBots, subscriptionCost, subscriptionLimitOrders, subscriptionSingleLimitOrders } from "constants/subscriptions";

import React, { useEffect, useMemo } from "react";

import { Check } from "@mui/icons-material";
import {
  TableContainer,
  Table,
  TableRow,
  TableCell,
  Typography,
  Box,
  useMediaQuery,
  Link,
  Stack,
} from "@mui/material";
import useTheme from "@mui/material/styles/useTheme";
import Panel from "components/elements/Panel";
import { Diamond, Rocket } from "components/icons";
import { Credit } from "components/icons/components/Credit";
import { fontWeight } from "config/Theme/options";
import { useMUIThemeModeContext } from "context/MUIThemeModeContext";
import { Trans, useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import { SubscriptionType } from "types/enums";
import { ConditionalWrapper, getColorByMode } from "utils";

import ModalDowngradingFail from "./ModalDowngradingFail";
import ModalNotEnoughCredits from "./ModalNotEnoughCredits";
import { useSubscription } from "./module/useSubscription";
import { SubscriptionButton, TableBody, TableHead } from "./styles";

interface TableContent {
  id: SubscriptionType;
  color: string;
  currencyPairs: string;
  dcaFrequency: string;
  targetValue: string;
  fillLimitOrders: string;
  btcWithdrawal: string;
  maxActiveBots: string;
  gridStrategyMinPercentStep: string;
  dashboardStrategiesDetails: string;
  dashboardStrategiesSummary: string;
  cashbackSatsAvailable: string;
  dynamicDcaOrderValue: string;
  exitStrategy: string;
  manualOrders: string;
  detailedDcaStats: string;
  limitOrdersDistance: string;
}

const tiers: TableContent[] = [
  {
    id: SubscriptionType.FREE,
    color: "gray",
    currencyPairs: "currencyPairsFree",
    dcaFrequency: "dcaFrequencyFree",
    targetValue: "blankPlaceholder",
    fillLimitOrders: "fillLimitOrdersFree",
    btcWithdrawal: "blankPlaceholder",
    maxActiveBots: "1",
    gridStrategyMinPercentStep: "2,5",
    dashboardStrategiesDetails: "blankPlaceholder",
    dashboardStrategiesSummary: "blankPlaceholder",
    cashbackSatsAvailable: "blankPlaceholder",
    dynamicDcaOrderValue: "blankPlaceholder",
    exitStrategy: "exitStrategyFree",
    manualOrders: "blankPlaceholder",
    detailedDcaStats: "blankPlaceholder",
    limitOrdersDistance: "1 %",
  },
  {
    id: SubscriptionType.PREMIUM,
    color: "primary",
    currencyPairs: "currencyPairsPremium",
    dcaFrequency: "dcaFrequencyPremium",
    targetValue: "yes",
    fillLimitOrders: "fillLimitOrdersPremium",
    btcWithdrawal: "yes",
    maxActiveBots: "4",
    gridStrategyMinPercentStep: "1",
    dashboardStrategiesDetails: "yes",
    dashboardStrategiesSummary: "yes",
    cashbackSatsAvailable: "10 %",
    dynamicDcaOrderValue: "yes",
    exitStrategy: "exitStrategyPremium",
    manualOrders: "yes",
    detailedDcaStats: "yes",
    limitOrdersDistance: "1 %",
  },
  {
    id: SubscriptionType.PRO,
    color: "text.primary",
    currencyPairs: "currencyPairsPremium",
    dcaFrequency: "dcaFrequencyPro",
    targetValue: "yes",
    fillLimitOrders: "fillLimitOrdersPremium",
    btcWithdrawal: "yes",
    maxActiveBots: "10",
    gridStrategyMinPercentStep: "1",
    dashboardStrategiesDetails: "yes",
    dashboardStrategiesSummary: "yes",
    cashbackSatsAvailable: "20 % + 10 % affiliate",
    dynamicDcaOrderValue: "yes",
    exitStrategy: "exitStrategyPremium",
    manualOrders: "yes",
    detailedDcaStats: "yes",
    limitOrdersDistance: "0,25 %",
  },
];

interface Props {
  cta?: boolean;
  backgroundOnMobile?: boolean;
}

const SubscriptionsPage: React.FC<Props> = ({ cta = true, backgroundOnMobile, children }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { colors } = useMUIThemeModeContext();
  const {
    actualSubscription,
    isLoading,
    setIsLoading,
    modalNotEnoughCreditsOpen,
    modalDowngradingFailOpen,
    subscriptionResponse,
    onModalDowngradingFailClose,
    onModalNotEnoughCredits,
    changeSubscription
  } = useSubscription();

  const currencyPairsListLink = "/menove-pary";
  const dcaStrategyTutorialLink = "/proc-bitcoin/stosuj-cz-navod-krok-za-krokem";
  const cashbackSatsTutorialLink = "/proc-bitcoin/cashback-saty";
  const dynamicDcaStrategyTutorialLink = "/proc-bitcoin/dynamicka-dca-strategie";
  const gridStrategyTutorialLink = "/proc-bitcoin/predstaveni-grid-strategie";

  const paidTierCosts = useMemo(
    () => tiers.slice(1).map((tier) => subscriptionCost[tier.id]),
    [tiers]
  );

  useEffect(() => {
    if(isLoading) setIsLoading(false);
  }, [actualSubscription])

  function renderRow(name: React.ReactNode, row: any[]) {
    return (
      <TableRow>
        <TableCell align="left">{name}</TableCell>
        {row.map((value, index) => (
          <TableCell key={index} align="center">
            {value}
          </TableCell>
        ))}
      </TableRow>
    );
  }

  function renderSubscriptionButton(subscriptionType: SubscriptionType) {
    return (
      <SubscriptionButton
        subscriptiontype={subscriptionType}
        disabled={isLoading || subscriptionType === actualSubscription}
        onClick={() => changeSubscription(subscriptionType)}>
        {subscriptionType === actualSubscription ? t("activeAlready") : t("activate")}
      </SubscriptionButton>
    );
  }

  const renderSubscriptionHeader = (subscriptionType: SubscriptionType) => {
    let color = colors.dustyMint;
    let icon = null;

    if (subscriptionType === SubscriptionType.PREMIUM) {
      color = colors.purple400;
      icon = <Rocket sx={{ fontSize: "1.25rem" }} />;
    } else if (subscriptionType === SubscriptionType.PRO) {
      color = getColorByMode(theme, colors.deepMidnightBlue, colors.gray800);
      icon = <Diamond sx={{ fontSize: "1.25rem" }} />;
    }

    return (
      <Stack spacing={1}>
        <Stack spacing={1} direction={"row"} justifyContent="center" alignItems="center" color={color}>
          {icon}
          <Typography color="inherit" sx={{ fontWeight: fontWeight.semiBold }}>
            {t(subscriptionType)}
          </Typography>
        </Stack>
        {cta && renderSubscriptionButton(subscriptionType)}
      </Stack>
    );
  };

  return (
    <>
      {isMobile && (
        <>
          {children && <Panel sx={{ mb: 4, p: 3, pb: 2 }}>{children}</Panel>}
          {tiers.map((tier, index) => (
            <ConditionalWrapper
              key={index}
              condition={!!backgroundOnMobile}
              wrapper={(children) => <Panel sx={{ pt: 1, pb: 3, px: 1, mb: 2 }}>{children}</Panel>}>
              <Box sx={{ mt: 4 }}>
                {renderSubscriptionHeader(tier.id)}
                <TableContainer sx={{ mt: 2 }}>
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell >{t("priceForOneMonth")}</TableCell>
                        <TableCell sx={{ overflow: "hidden" }} align="right">
                          {index === 0 && t("foreverFree")}
                          {index > 0 && (
                            <Box
                              sx={{
                                fontWeight: 400,
                                display: "inline-flex",
                                justifyContent: "end",
                                alignItems: "center",
                              }}>
                              <strong>{subscriptionCost[tier.id]}</strong>
                              <Credit sx={{ marginLeft: "4px" }} />
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          {t("dcaStrategiesCount")}
                          <br/>
                          <small>{t("dcaStrategiesCountLabel")} ‑&nbsp;<Link to={dcaStrategyTutorialLink} component={RouterLink}>
                            <Trans i18nKey="showTutorialLink"/>
                          </Link>
                          </small>
                        </TableCell>
                        <TableCell align="right">
                          {subscriptionDcaBots[tier.id]}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          {t("dynamicDcaOrderValue")}&nbsp;💎
                          <br />
                          <small>{t("dynamicDcaOrderValueLabel")} ‑&nbsp;<Link to={dynamicDcaStrategyTutorialLink} component={RouterLink}>
                            <Trans i18nKey="showTutorialLink"/>
                          </Link>
                          </small>
                        </TableCell>
                        <TableCell align="right">
                          {tier.dynamicDcaOrderValue === "yes" ? (
                            <strong>
                              <Check style={{ color: colors.success, fontSize: "18px" }} />
                            </strong>
                          ) : (
                            <>
                              {t(tier.dynamicDcaOrderValue)}
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                        {t("cashbackSatsAvailable")}&nbsp;🧡
                          <br />
                          <small>{t("cashbackSatsAvailableLabel")} ‑&nbsp;<Link to={cashbackSatsTutorialLink} component={RouterLink}>
                            <Trans i18nKey="cashbackSatsConditionsLink"/>
                          </Link>
                          </small>
                        </TableCell>
                        <TableCell align="right">
                          {t(tier.cashbackSatsAvailable)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          {t("manualOrders")}
                          <br/>
                          <small>{t("manualOrdersLabel")}</small>
                        </TableCell>
                        <TableCell align="right">
                          {tier.cashbackSatsAvailable === "yes" ? (
                            <strong>
                              <Check style={{ color: colors.success, fontSize: "18px" }} />
                            </strong>
                          ) : (
                            <>
                              {t(tier.manualOrders)}
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          {t("detailedDcaStats")}
                          <br/>
                          <small>{t("forDcaBot")}</small>
                        </TableCell>
                        <TableCell align="right">
                          {tier.detailedDcaStats === "yes" ? (
                            <strong>
                              <Check style={{ color: colors.success, fontSize: "18px" }} />
                            </strong>
                          ) : (
                            <>
                              {t(tier.detailedDcaStats)}
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          {t("maxActiveBots")}
                          <br/>
                          <small>
                            {t("maxActiveBotsLabel")} -&nbsp;<Link to={gridStrategyTutorialLink} component={RouterLink}>
                            <Trans i18nKey="showTutorialLink"/>
                          </Link>
                          </small>
                        </TableCell>
                        <TableCell align="right">
                          {t(tier.maxActiveBots)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          {t("gridStrategyMinPercentStep")}
                          <br />
                          <small>{t("gridStrategyMinPercentStepLabel")}</small>
                        </TableCell>
                        <TableCell align="right">
                          {t(tier.gridStrategyMinPercentStep)} %
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          {t("currencyPairs")}
                          <br />
                          <small>
                            <Link to={currencyPairsListLink} component={RouterLink}>
                              <Trans i18nKey="currencyPairsList" />
                            </Link>
                          </small>
                        </TableCell>
                        <TableCell align="right">
                          <Trans
                            i18nKey={tier.currencyPairs}
                            components={{ a: <Link to={currencyPairsListLink} component={RouterLink} /> }}
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>{t("dcaFrequency")}</TableCell>
                        <TableCell align="right">
                          {t(tier.dcaFrequency)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          {t("fillLimitOrdersFeature")}
                          <br />
                          <small>{t("forDcaBot")}</small>
                        </TableCell>
                        <TableCell align="right">
                          {t(tier.fillLimitOrders)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          {t("limitOrdersCount")}
                          <br />
                          <small>{t("inASingleOrder")}</small>
                        </TableCell>
                        <TableCell align="right">
                          {subscriptionLimitOrders[tier.id]}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          {t("limitOrdersDistance")}
                          <br />
                          <small>{t("inASingleOrder")}</small>
                        </TableCell>
                        <TableCell align="right">
                          {t(tier.limitOrdersDistance)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell >
                          {t("singleLimitOrdersCount")}
                          <br />
                          <small>{t("inDcaBot")}</small>
                        </TableCell>
                        <TableCell align="right">
                          {subscriptionSingleLimitOrders[tier.id]}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          {t("targetValueFeature")}
                          <br />
                          <small>{t("forDcaBot")}</small>
                        </TableCell>
                        <TableCell align="right">
                          {tier.targetValue === "yes" ? (
                            <strong>
                              <Check style={{ color: colors.success, fontSize: "18px" }} />
                            </strong>
                          ) : (
                            <>
                              {t(tier.targetValue)}
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          {t("btcWithdrawal")}
                          <br />
                          <small>{t("toAddress")}</small>
                        </TableCell>
                        <TableCell align="right">
                          {tier.btcWithdrawal === "yes" ? (
                            <strong>
                              <Check style={{ color: colors.success, fontSize: "18px" }} />
                            </strong>
                          ) : (
                            <>
                              {t(tier.btcWithdrawal)}
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          {t("dashboardStrategiesDetails")}
                          <br />
                          <small>{t("dashboardStrategiesDetailsOnDashboard")}</small>
                        </TableCell>
                        <TableCell align="right">
                          {tier.dashboardStrategiesDetails === "yes" ? (
                            <strong>
                              <Check style={{ color: colors.success, fontSize: "18px" }} />
                            </strong>
                          ) : (
                            <>
                              {t(tier.dashboardStrategiesDetails)}
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          {t("dashboardStrategiesSummary")}
                          <br />
                          <small>{t("dashboardStrategiesSummaryOnDashboard")}</small>
                        </TableCell>
                        <TableCell align="right">
                          {tier.dashboardStrategiesSummary === "yes" ? (
                            <strong>
                              <Check style={{ color: colors.success, fontSize: "18px" }} />
                            </strong>
                          ) : (
                            <>
                              {t(tier.dashboardStrategiesSummary)}
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          {t("exitStrategyParams")}
                          <br />
                          <small>{t("exitStrategyParamsLabel")}</small>
                        </TableCell>
                        <TableCell align="right">
                          {t(tier.exitStrategy)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </ConditionalWrapper>
          ))}
        </>
      )}
      {!isMobile && (
        <Panel sx={{ mb: 4, p: 3 }}>
          {children}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell align="right" sx={{ width: "40%" }} />
                  <TableCell align="center" sx={{ width: "20%" }}>
                    {renderSubscriptionHeader(SubscriptionType.FREE)}
                  </TableCell>
                  <TableCell align="center" sx={{ width: "20%" }}>
                    {renderSubscriptionHeader(SubscriptionType.PREMIUM)}
                  </TableCell>
                  <TableCell align="center" sx={{ width: "20%" }}>
                    {renderSubscriptionHeader(SubscriptionType.PRO)}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell align="left">{t("priceForOneMonth")}</TableCell>
                  <TableCell align="center">{t("foreverFree")}</TableCell>
                  {paidTierCosts.map((value, index) => (
                    <TableCell key={index} align="center">
                      <Box sx={{ fontWeight: 400, display: "flex", justifyContent: "center", alignItems: "center" }}>
                        <strong>{value}</strong>
                        <Credit sx={{ marginLeft: "4px" }} />
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>

                {renderRow(
                  <>
                    {t("dcaStrategiesCount")}
                    <br/>
                    <small>{t("dcaStrategiesCountLabel")} -&nbsp;<Link to={dcaStrategyTutorialLink} component={RouterLink}>
                      <Trans i18nKey="showTutorialLink"/>
                    </Link>
                    </small>
                  </>,
                  tiers.map((tier) => subscriptionDcaBots[tier.id])
                )}
                {renderRow(
                  <>
                    {t("dynamicDcaOrderValue")}&nbsp;💎
                    <br/>
                    <small>
                      {t("dynamicDcaOrderValueLabel")} -&nbsp;<Link to={dynamicDcaStrategyTutorialLink} component={RouterLink}>
                      <Trans i18nKey="showTutorialLink"/>
                    </Link>
                    </small>
                  </>,
                  tiers.map((tier) =>
                    tier.dynamicDcaOrderValue === "yes" ? (
                      <Check style={{ color: colors.success, fontSize: "18px" }} />
                    ) : (
                      <>{t(tier.dynamicDcaOrderValue)}</>
                    )
                  )
                )}
                {renderRow(
                  <>
                    {t("cashbackSatsAvailable")}&nbsp;🧡
                    <br/>
                    <small>
                      {t("cashbackSatsAvailableLabel")} -&nbsp;<Link to={cashbackSatsTutorialLink} component={RouterLink}>
                      <Trans i18nKey="cashbackSatsConditionsLink"/>
                    </Link>
                    </small>
                  </>,
                  tiers.map((tier) =>
                    tier.cashbackSatsAvailable === "yes" ? (
                      <Check style={{ color: colors.success, fontSize: "18px" }} />
                    ) : (
                      <>{t(tier.cashbackSatsAvailable)}</>
                    )
                  )
                )}
                {renderRow(
                  <>
                    {t("manualOrders")}
                    <br/>
                    <small>{t("manualOrdersLabel")}</small>
                  </>,
                  tiers.map((tier) =>
                    tier.manualOrders === "yes" ? (
                      <Check style={{ color: colors.success, fontSize: "18px" }} />
                    ) : (
                      <>{t(tier.manualOrders)}</>
                    )
                  )
                )}
                {renderRow(
                  <>
                    {t("detailedDcaStats")}
                    <br/>
                    <small>{t("forDcaBot")}</small>
                  </>,
                  tiers.map((tier) =>
                    tier.detailedDcaStats === "yes" ? (
                      <Check style={{ color: colors.success, fontSize: "18px" }} />
                    ) : (
                      <>{t(tier.detailedDcaStats)}</>
                    )
                  )
                )}
                {renderRow(
                  <>
                    {t("maxActiveBots")}
                    <br/>
                    <small>
                      {t("maxActiveBotsLabel")} -&nbsp;<Link to={gridStrategyTutorialLink} component={RouterLink}>
                      <Trans i18nKey="showTutorialLink"/>
                    </Link>
                    </small>
                  </>,
                  tiers.map((tier) => t(tier.maxActiveBots))
                )}
                {renderRow(
                  <>
                    {t("gridStrategyMinPercentStep")}
                    <br />
                    <small>{t("gridStrategyMinPercentStepLabel")}</small>
                  </>,
                  tiers.map((tier) => (
                    <>
                      {t(tier.gridStrategyMinPercentStep)} {"%"}
                    </>
                  ))
                )}
                {renderRow(
                  <>
                    {t("currencyPairs")}
                    <br />
                    <small>
                      <Link to={currencyPairsListLink} component={RouterLink}>
                        <Trans i18nKey="currencyPairsList" />
                      </Link>
                    </small>
                  </>,
                  tiers.map((tier) => (
                    <Trans
                      i18nKey={tier.currencyPairs}
                      components={{ a: <Link to={currencyPairsListLink} component={RouterLink} /> }}
                    />
                  ))
                )}
                {renderRow(
                  t("dcaFrequency"),
                  tiers.map((tier) => t(tier.dcaFrequency))
                )}
                {renderRow(
                  <>
                    {t("fillLimitOrdersFeature")}
                    <br />
                    <small>{t("forDcaBot")}</small>
                  </>,
                  tiers.map((tier) => t(tier.fillLimitOrders))
                )}
                {renderRow(
                  <>
                    {t("limitOrdersCount")}
                    <br />
                    <small>{t("inASingleOrder")}</small>
                  </>,
                  tiers.map((tier) => subscriptionLimitOrders[tier.id])
                )}
                {renderRow(
                  <>
                    {t("limitOrdersDistance")}
                    <br />
                    <small>{t("inASingleOrder")}</small>
                  </>,
                  tiers.map((tier) => t(tier.limitOrdersDistance))
                )}
                {renderRow(
                  <>
                    {t("singleLimitOrdersCount")}
                    <br />
                    <small>{t("inDcaBot")}</small>
                  </>,
                  tiers.map((tier) => subscriptionSingleLimitOrders[tier.id])
                )}
                {renderRow(
                  <>
                    {t("targetValueFeature")}
                    <br />
                    <small>{t("forDcaBot")}</small>
                  </>,
                  tiers.map((tier) =>
                    tier.targetValue === "yes" ? (
                      <Check style={{ color: colors.success, fontSize: "18px" }} />
                    ) : (
                      <>{t(tier.targetValue)}</>
                    )
                  )
                )}
                {renderRow(
                  <>
                    {t("btcWithdrawal")}
                    <br />
                    <small>{t("toAddress")}</small>
                  </>,
                  tiers.map((tier) =>
                    tier.btcWithdrawal === "yes" ? (
                      <Check style={{ color: colors.success, fontSize: "18px" }} />
                    ) : (
                      <>{t(tier.btcWithdrawal)}</>
                    )
                  )
                )}
                {renderRow(
                  <>
                    {t("dashboardStrategiesDetails")}
                    <br />
                    <small>{t("dashboardStrategiesDetailsOnDashboard")}</small>
                  </>,
                  tiers.map((tier) =>
                    tier.dashboardStrategiesDetails === "yes" ? (
                      <Check style={{ color: colors.success, fontSize: "18px" }} />
                    ) : (
                      <>{t(tier.dashboardStrategiesDetails)}</>
                    )
                  )
                )}
                {renderRow(
                  <>
                    {t("dashboardStrategiesSummary")}
                    <br />
                    <small>{t("dashboardStrategiesSummaryOnDashboard")}</small>
                  </>,
                  tiers.map((tier) =>
                    tier.dashboardStrategiesSummary === "yes" ? (
                      <Check style={{ color: colors.success, fontSize: "18px" }} />
                    ) : (
                      <>{t(tier.dashboardStrategiesSummary)}</>
                    )
                  )
                )}
                {renderRow(
                  <>
                    {t("exitStrategyParams")}
                    <br />
                    <small>{t("exitStrategyParamsLabel")}</small>
                  </>,
                  tiers.map((tier) => t(tier.exitStrategy))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Panel>
      )}
      {modalDowngradingFailOpen && (
        <ModalDowngradingFail
          open={modalDowngradingFailOpen}
          onClose={onModalDowngradingFailClose}
          data={subscriptionResponse}
        />
      )}
      {modalNotEnoughCreditsOpen && (
        <ModalNotEnoughCredits open={modalNotEnoughCreditsOpen} onClose={onModalNotEnoughCredits} />
      )}
    </>
  );
};

export default SubscriptionsPage;
